import AlertBanner from "molecules/alerts/alert-banner";
import AlertLevels from "constants/alert-levels";
import Button from "atoms/buttons/button";
import Form from "molecules/forms/form";
import Icon from "atoms/icons/icon";
import React, { useCallback, useEffect, useRef, useState } from "react";
import StringUtils from "utilities/string-utils";
import TextArea from "atoms/forms/text-area";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";
import uuid from "uuid";
import { AriaRole } from "utilities/enumerations/aria-role";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { CollectionUtils } from "@rsm-hcd/javascript-core";
import { ConversationStateStatus } from "utilities/atoms/conversations/current-conversation/conversation-atom.reducer";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { encodingForModel } from "js-tiktoken";
import { useConversation } from "utilities/atoms/conversations/current-conversation/use-conversation.hook";
import { useLocalization } from "utilities/hooks/use-localization";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import Tooltip, { TooltipPlacement } from "molecules/tooltips/tooltip";
import ChatOfflineTooltipContent from "organisms/modals/chat-modal/chat-offline-tooltip-content";
import ChatLengthLimitedTooltipContent from "organisms/modals/chat-modal/chat-length-limited-tooltip-content";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

export interface ChatPromptProps {
    newChatStarted?: boolean;
    prompt?: string;
    handleSetPrompt: React.Dispatch<React.SetStateAction<string>>;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS = "c-chat-modal-prompt";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ChatPrompt: React.FC<ChatPromptProps> = ({
    newChatStarted = false,
    prompt = "",
    handleSetPrompt,
}) => {
    const { t } = useLocalization();
    const [defaultId] = useState(`chat-form-${uuid.v4()}`);
    const [maxTokenLimitReached, setMaxTokenLimitReached] = useState(false);
    const isMobileView = useBreakpointRanges();
    const { isOnline } = useNetworkInformation();

    const {
        sendPrompt,
        status,
        cancelPrompt,
        clearConversation,
        currentConversation,
        lastAssistantMessageLengthLimited,
    } = useConversation();
    const sendMessage = useCallback(
        (value?: string) => {
            const isTokenLimitExceeded = (prompt: string) => {
                const encoding = encodingForModel("gpt-4o");
                const tokenCount = encoding.encode(prompt).length;
                setMaxTokenLimitReached(tokenCount >= 15000);
                return tokenCount >= 15000;
            };

            const text = value ?? prompt;

            const tokenLimitExceeded = isTokenLimitExceeded(text);

            if (!tokenLimitExceeded && text.trim()) {
                sendPrompt(text.trim());
                handleSetPrompt("");
                inputRef.current?.focus();
            }
        },
        [handleSetPrompt, prompt, sendPrompt]
    );

    const chatMaxLimitReached =
        status === ConversationStateStatus.ConversationMaxLengthReached;

    const inputRef = useRef<HTMLTextAreaElement>(null);

    const adjustTextareaHeight = useCallback(() => {
        if (!isMobileView || !inputRef.current) {
            return;
        }

        const textarea = inputRef.current;
        textarea.style.height = "auto";
        const scrollHeight = textarea.scrollHeight;
        const maxHeight = 150;
        const minHeight = 40;
        const newHeight = Math.min(
            Math.max(scrollHeight, minHeight),
            maxHeight
        );
        textarea.style.height = `${newHeight}px`;
    }, [isMobileView]);

    const handleTextareaChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            handleSetPrompt(e.target.value);
            adjustTextareaHeight();
        },
        [handleSetPrompt, adjustTextareaHeight]
    );

    const handleSubmitButton = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        event.preventDefault();
        event.stopPropagation();
        sendMessage();
    };

    // Handle Enter/Shift+Enter in textarea
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    useEffect(() => {
        if (newChatStarted && inputRef.current) {
            inputRef.current.focus();
        }
    }, [newChatStarted]);

    useEffect(() => {
        if (isMobileView) {
            adjustTextareaHeight();
        } else if (inputRef.current) {
            inputRef.current.style.height = "";
        }
    }, [prompt, isMobileView, adjustTextareaHeight]);

    useEffect(() => {
        if (!isMobileView && inputRef.current) {
            inputRef.current.style.height = "";
        }
    }, [isMobileView]);

    return (
        <div className={BASE_CLASS}>
            <Tooltip
                disabled={isOnline && !lastAssistantMessageLengthLimited}
                content={
                    lastAssistantMessageLengthLimited ? (
                        <ChatLengthLimitedTooltipContent />
                    ) : (
                        <ChatOfflineTooltipContent />
                    )
                }
                durationInMs={0}
                hideOnClick={true}
                placement={TooltipPlacement.AutoStart}>
                <Form
                    ariaRole={AriaRole.Search}
                    onSubmit={() => {}}
                    cssClassName={`${lastAssistantMessageLengthLimited ? " -max-token-limit" : ""}`}>
                    {chatMaxLimitReached ? (
                        <div className={`${BASE_CLASS}__max`}>
                            <AlertBanner alertLevel={AlertLevels.Error}>
                                {t("chat-max-limit-warning")}
                                <Button
                                    disabled={!isOnline}
                                    size={ButtonSizes.Small}
                                    cssClassName={`${BASE_CLASS}__max-new-chat`}
                                    style={ButtonStyles.Primary}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        clearConversation();
                                    }}>
                                    {t("start-new-chat")}
                                </Button>
                            </AlertBanner>
                        </div>
                    ) : (
                        <div className={`${BASE_CLASS}__input-wrapper`}>
                            <div className={`${BASE_CLASS}__input-container`}>
                                <TextArea
                                    disabled={
                                        !isOnline ||
                                        lastAssistantMessageLengthLimited
                                    }
                                    ref={inputRef}
                                    id={defaultId}
                                    onChange={handleTextareaChange}
                                    onKeyDown={handleKeyDown}
                                    placeholder={
                                        CollectionUtils.isEmpty(
                                            currentConversation?.messages
                                        )
                                            ? t("start-a-conversation")
                                            : t("continue-a-conversation")
                                    }
                                    value={prompt}
                                    rows={4}
                                    spellcheck={false}
                                    autofocus={true}
                                />
                                {status ===
                                ConversationStateStatus.StreamingMessage ? (
                                    <Button
                                        disabled={!isOnline}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            cancelPrompt();
                                        }}
                                        cssClassName={`${BASE_CLASS}__send-button`}
                                        size={ButtonSizes.Small}
                                        style={ButtonStyles.Primary}>
                                        <Icon
                                            size={IconSizes.Large}
                                            type={Icons.Stop}
                                        />
                                    </Button>
                                ) : (
                                    <Button
                                        accessibleText={t("send-prompt")}
                                        onClick={handleSubmitButton}
                                        cssClassName={`${BASE_CLASS}__send-button`}
                                        size={ButtonSizes.Small}
                                        style={
                                            StringUtils.isEmpty(prompt)
                                                ? ButtonStyles.Tertiary
                                                : ButtonStyles.Primary
                                        }
                                        disabled={
                                            StringUtils.isEmpty(prompt) ||
                                            maxTokenLimitReached ||
                                            !isOnline ||
                                            lastAssistantMessageLengthLimited
                                        }>
                                        <Icon
                                            cssClassName={"-send-icon"}
                                            size={IconSizes.Medium}
                                            type={Icons.SendArrow}
                                        />
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </Form>
            </Tooltip>
            {maxTokenLimitReached && (
                <div className={`${BASE_CLASS}__max-message`}>
                    <AlertBanner alertLevel={AlertLevels.Error}>
                        {t("message-too-long")}
                    </AlertBanner>
                </div>
            )}
            {!isOnline && (
                <div className={`${BASE_CLASS}__chat-unavailable`}>
                    <AlertBanner alertLevel={AlertLevels.Error}>
                        {t("chat-offline-description")}
                    </AlertBanner>
                </div>
            )}
            {lastAssistantMessageLengthLimited && (
                <div className={`${BASE_CLASS}__max-message`}>
                    <AlertBanner alertLevel={AlertLevels.Error}>
                        {t("chat-length-limit-description")}
                    </AlertBanner>
                </div>
            )}
            <div className={`${BASE_CLASS}__disclaimer`}>
                {t("chat-modal-prompt-disclaimer-start")}
            </div>
        </div>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default ChatPrompt;

// #endregion Exports
